import { useState, useEffect, useCallback, useMemo, type JSX } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type CellStyle,
  type ColDef,
  type ICellRendererParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { ChevronLeft, Copy, Check, AlertTriangle, Info } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useParamStore } from '../../hooks/useParamStore';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import {
  createNotification,
  maybeFireLowCreditWarning,
  notifySubmitterAndAdmins,
} from '../../lib/notifications';
import { triggerTicker } from '../../lib/activityTicker';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import NotesPanel from '../../components/notes/NotesPanel';
// Day 76 P5.1 Phase 9B — PATH_LABEL retired; pathLabel helper below
// derives from useSettingsConfig().activePaths.
import CancelPOModal, { type CancelPOPayload } from '../../components/purchasing/CancelPOModal';
import { Toast, type ToastState } from '../../components/ui/Toast';
import ConcurrentEditBanner from '../../components/ui/ConcurrentEditBanner';
import ActualCostsModal, {
  type ActualCostFormPayload,
} from '../../components/purchasing/ActualCostsModal';
import { fetchReceivingHistory, type ReceivingHistoryRow } from '../../lib/receiving';
import type { POActualCost, POStatus } from '../../types';
import type { PostgrestError } from '@supabase/supabase-js';
import { extractEnvelope } from '../../lib/extractEnvelope';
import { ProvenanceTooltip } from '../../components/ui/ProvenanceTooltip';
import type { ProvenanceMap } from '../../components/ui/ProvenanceIndicator';
import type {
  CalcProfitabilitySalePriceSource,
  CalcProfitabilityVelocitySource,
} from '../../lib/engineFlags';

ModuleRegistry.registerModules([AllCommunityModule]);

// Loose RPC caller — chain_cancel_po, create_credit_ledger_entry,
// generate_po_number are called with dynamic args here.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmt$(v: number | string | null | undefined): string {
  if (v == null || v === '') return '\u2014';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return '\u2014';
  return new Date(v).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '\u2014';
  return `${Number(v).toFixed(1)}%`;
}

/**
 * Convert a Blob to a base64 string suitable for the send-po-email Edge
 * Function payload. Strips the data:application/pdf;base64, prefix that
 * FileReader.readAsDataURL adds \u2014 Resend's attachment.content expects a
 * raw base64 string.
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('blob read failed'));
    reader.onload = () => {
      const dataUrl = String(reader.result ?? '');
      const idx = dataUrl.indexOf(',');
      resolve(idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl);
    };
    reader.readAsDataURL(blob);
  });
}

/** Compact relative-time formatter for the "Sent X ago to Y" badge. */
function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Status config ───────────────────────────────────────────────────────────

interface StatusConfig {
  label: string;
  cls: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  draft: { label: 'Draft', cls: 'bg-surface-2 text-ink-muted' },
  pending_review: { label: 'Pending Review', cls: 'bg-amber-400/10 text-amber-400' },
  rejected: { label: 'Rejected', cls: 'bg-red-400/10 text-red-400' },
  approved: { label: 'Approved', cls: 'bg-blue-400/10 text-blue-400' },
  ordered: { label: 'Ordered', cls: 'bg-purple-400/10 text-purple-400' },
  awaiting_invoice: { label: 'Awaiting Invoice', cls: 'bg-yellow-400/10 text-yellow-400' },
  processing: { label: 'Processing', cls: 'bg-cyan-400/10 text-cyan-400' },
  invoice_received: { label: 'Invoice Received', cls: 'bg-teal-400/10 text-teal-400' },
  partially_received: { label: 'Partial', cls: 'bg-green-400/10 text-green-400' },
  received: { label: 'Received', cls: 'bg-green-400/15 text-green-300 font-semibold' },
  closed: { label: 'Closed', cls: 'bg-surface-2 text-ink-muted' },
  superseded: { label: 'Superseded', cls: 'bg-surface-2 text-ink-muted italic' },
  cancelled: { label: 'Cancelled', cls: 'bg-surface-2 text-ink-muted line-through' },
  discrepancy: { label: 'Discrepancy', cls: 'bg-red-400/10 text-red-400' },
};

const EDITABLE_STATUSES: POStatus[] = ['draft', 'rejected'];

// Day 56 — statuses where actual costs may be logged. PO has become a
// real-world commitment (Ordered onward) and not yet closed/cancelled.
// Day 57 — PDF download + email-to-distributor visibility window. PO must be
// signed-off (approved or later) and not in a terminal cancelled/superseded
// state. Closed and received are intentionally INCLUDED — historical re-print
// + audit-copy email are valid use cases.
const STATUSES_PDF_VISIBLE: POStatus[] = [
  'approved',
  'ordered',
  'awaiting_invoice',
  'processing',
  'invoice_received',
  'partially_received',
  'received',
  'closed',
];

const ACTUALS_WINDOW_STATUSES: POStatus[] = [
  'ordered',
  'awaiting_invoice',
  'processing',
  'invoice_received',
  'partially_received',
  'received',
];

const READ_ONLY_MESSAGES: Record<string, string> = {
  approved:
    'Soft reserve applied. Place the order with the distributor, then click Mark Ordered to advance the PO.',
  ordered:
    'Ordered with distributor. Mark Awaiting Invoice once the order is acknowledged and you are tracking invoice arrival.',
  awaiting_invoice: 'Waiting for distributor invoice. Mark Processing when the invoice arrives.',
  processing: 'Invoice is being processed. Mark Invoice Received when actual costs are finalized.',
  invoice_received: 'Inventory expected soon. Click Begin Receiving to enter received quantities.',
  partially_received:
    'Receiving in progress. Click Continue Receiving to log additional inventory as it arrives.',
  received: 'All inventory has been received.',
  closed: 'This PO is closed.',
  superseded: 'This PO has been superseded by a revision.',
  cancelled: 'This PO has been cancelled.',
};

// ── Row types ───────────────────────────────────────────────────────────────

interface PORow {
  id: string;
  po_number: string;
  status: POStatus;
  distributor_id: string | null;
  total_cost: number | null;
  expected_profit: number | null;
  total_units: number | null;
  total_skus: number | null;
  order_date: string | null;
  expected_delivery_date: string | null;
  fulfillment_path: string | null;
  parent_po_id: string | null;
  revision_suffix: string | null;
  submitted_by: string | null;
  submitted_at: string | null;
  created_by: string | null;
  external_transactions: ExternalTransactions | null;
  distributors?: { name: string | null; estimated_tax_pct?: number | null } | null;
}

/**
 * Day 57 — outbound action audit trail keyed by action type. Single-source
 * store for "what's been sent out about this PO and when". Per ADR Decision
 * 32 cluster — the canonical forward shape; the 6 dormant scalar columns
 * (sent_at, sent_by, po_pdf_path, distributor_po_number,
 * distributor_submitted_at, acknowledged_at) are deferred-deprecation per
 * the BACKLOG POST-LAUNCH item.
 */
interface ExternalTransactions {
  distributor_po_sent?: { sent_at: string; to_email: string; method: 'email' };
  distributor_po_acknowledged?: { at: string; received_po_number?: string };
  accounting_system_synced?: { at: string };
}

interface DistributorContact {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}

interface LineItemRow {
  id: string;
  isg_sku: string | null;
  product_id: string | null;
  qty_ordered: number | null;
  cost_per_unit: number | null;
  total_cost: number | null;
  fulfillment_path: string | null;
  est_profit_per_unit: number | null;
  est_total_profit: number | null;
  title?: string;
  brand?: string;
}

interface RevisionRef {
  id: string;
  po_number: string;
}

type ConfirmAction =
  | 'submit'
  | 'approve'
  | 'editRequired'
  | 'markOrdered'
  | 'markAwaitingInvoice'
  | 'markProcessing'
  | 'markInvoiceReceived'
  | 'closeShort'
  | null;

// ── PODetail page ───────────────────────────────────────────────────────────

export default function PODetail(): JSX.Element {
  const { poId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Day 64.5 V-ROUI: viewer hides every mutation in the action cluster
  // (Duplicate, Edit, Submit, status-transition buttons, Cancel, etc.).
  // Admin-only mutations (Approve/Reject/Mark Ordered/Edit Required/Mark
  // Awaiting Invoice + Add Actual Cost + PDF email) are already excluded
  // for viewer via the existing isAdmin guards. PDF Download stays
  // accessible behind its existing isAdmin guard (export-like, KEEP per
  // gating taxonomy — viewer doesn't reach it via admin gate anyway).
  const { user, isAdmin, isViewer, isDistRep } = useApp();
  const paramStore = useParamStore();
  // Day 76 P5.1 Phase 9B — path label lookup replacing hardcoded PATH_LABEL.
  // Falls back to path_key.toUpperCase() for historical-data carve-out
  // (deactivated/legacy paths not in fpc).
  const { activePaths } = useSettingsConfig();
  const pathLabel = useCallback(
    (path: string): string =>
      activePaths.find((p) => p.path_key === path)?.short_label ?? path.toUpperCase(),
    [activePaths]
  );

  const [po, setPo] = useState<PORow | null>(null);
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);
  // Per-fulfillment-path totals derived from po_line_item_allocations across
  // every line item on this PO. Used to render the Fulfillment Path summary
  // (B-R2-3) — replaces the old display that read po.fulfillment_path
  // directly. Empty map = legacy PO without allocation rows; falls back to
  // po.fulfillment_path. po.fulfillment_path remains the legacy fallback
  // until a full deprecation pass (post-launch).
  const [allocationTotals, setAllocationTotals] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [rejectNote, setRejectNote] = useState<string>('');
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [revisionNotifySubmitter, setRevisionNotifySubmitter] = useState<boolean>(true);
  // Day 57 — PO PDF download + email-to-distributor state.
  const [primaryContact, setPrimaryContact] = useState<DistributorContact | null>(null);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [pdfActionInFlight, setPdfActionInFlight] = useState<'download' | 'email' | null>(null);
  const [revision, setRevision] = useState<RevisionRef | null>(null);
  const [parentPo, setParentPo] = useState<RevisionRef | null>(null);
  const [copiedPO, setCopiedPO] = useState<boolean>(false);
  const [receivingHistory, setReceivingHistory] = useState<ReceivingHistoryRow[]>([]);
  // Day 51 Phase 5: invoice variance card. Populated when an invoice
  // attached to this PO has status = 'variance_flagged' (set by
  // rule_flag_invoice_variance on the invoice trigger).
  const [varianceInvoice, setVarianceInvoice] = useState<{
    id: string;
    amount: number | null;
    status: string;
  } | null>(null);

  // Day 56 item 5 — Actual Costs sidecar entries for this PO. Loaded via the
  // dedicated useEffect below (gated on !isDistRep — Architecture Rule 4 cost
  // data). Refreshes on save/delete by re-firing the load query.
  const [actualCosts, setActualCosts] = useState<POActualCost[]>([]);
  // Day 84.8b P3a — product_profitability source flags keyed by product_id.
  // Path A SELECT extension (F2 audit §3 disposition): fetches CURRENT-state
  // engine source flags per line item's product, for synthetic-envelope
  // construction at the Profit/Unit cellRenderer. Source flag refers to
  // current cache state; rendered value (est_profit_per_unit) is a SNAPSHOT
  // from po_line_items at PO submit time. Snapshot-vs-current divergence
  // surfaced operator-side via Lock 2 valueIsSnapshot=true → "Current source:
  // <name>" tooltip body prefix.
  const [productProvenance, setProductProvenance] = useState<
    Record<
      string,
      {
        sale_price_source: CalcProfitabilitySalePriceSource | null;
        velocity_source: CalcProfitabilityVelocitySource | null;
      }
    >
  >({});
  const [showActualCostModal, setShowActualCostModal] = useState<boolean>(false);
  const [editingActualCost, setEditingActualCost] = useState<POActualCost | null>(null);
  const [deleteCostTarget, setDeleteCostTarget] = useState<POActualCost | null>(null);

  // ── Load PO data ────────────────────────────────────────────
  const loadPO = useCallback(async (): Promise<void> => {
    setLoading(true);
    const { data, error } = await supabase
      .from('purchase_orders')
      .select('*, distributors:distributor_id(name, estimated_tax_pct)')
      .eq('id', poId ?? '')
      .single();
    if (error || !data) {
      setToast({ kind: 'error', message: 'Failed to load PO' });
      setLoading(false);
      return;
    }
    const poRow = data as unknown as PORow;
    setPo(poRow);

    // Load line items
    const { data: items } = await supabase
      .from('po_line_items')
      .select(
        'id, isg_sku, product_id, qty_ordered, cost_per_unit, total_cost, fulfillment_path, est_profit_per_unit, est_total_profit'
      )
      .eq('po_id', poId ?? '')
      .order('created_at', { ascending: true });
    const itemRows = (items ?? []) as unknown as LineItemRow[];
    setLineItems(itemRows);

    // Load per-path allocations for this PO's line items and aggregate to
    // a path → total map. Drives the Fulfillment Path display below; empty
    // map → render falls back to po.fulfillment_path (legacy POs).
    if (itemRows.length) {
      const lineItemIds = itemRows.map((i) => i.id);
      const { data: allocRows } = await supabase
        .from('po_line_item_allocations')
        .select('fulfillment_path, quantity')
        .in('line_item_id', lineItemIds);
      const totals: Record<string, number> = {};
      for (const row of (allocRows ?? []) as Array<{
        fulfillment_path: string | null;
        quantity: number | null;
      }>) {
        const path = row.fulfillment_path;
        const qty = Number(row.quantity) || 0;
        if (!path || qty <= 0) continue;
        totals[path] = (totals[path] || 0) + qty;
      }
      setAllocationTotals(totals);
    } else {
      setAllocationTotals({});
    }

    // Load product titles for line items
    if (itemRows.length) {
      const productIds = itemRows.map((i) => i.product_id).filter(Boolean) as string[];
      if (productIds.length) {
        const { data: products } = await supabase
          .from('products')
          .select('id, title, brand')
          .in('id', productIds);
        if (products) {
          const pMap: Record<string, { title: string | null; brand: string | null }> =
            Object.fromEntries(
              (products as { id: string; title: string | null; brand: string | null }[]).map(
                (p) => [p.id, { title: p.title, brand: p.brand }]
              )
            );
          setLineItems((prev) =>
            prev.map((i) => ({
              ...i,
              title: (i.product_id && pMap[i.product_id]?.title) || '\u2014',
              brand: (i.product_id && pMap[i.product_id]?.brand) || '\u2014',
            }))
          );
        }

        // Day 84.8b P3a \u2014 Path A SELECT extension against product_profitability
        // for current-state engine source flags. Keyed by product_id; each line
        // item has a known fulfillment_path so we deduplicate per (product_id,
        // path) pair via Map \u2014 keyed by pp's stored `marketplace` column
        // (public.marketplace enum, carries fpc path keys post-U9; renames only
        // when the enum dies, slice-B family). Filter by this PO's distributor_id \u2014
        // product_profitability rows are per-distributor scoped.
        if (poRow.distributor_id) {
          const linePaths = new Set(
            itemRows
              .map((i) => i.fulfillment_path)
              .filter((p): p is string => typeof p === 'string' && p.length > 0)
          );
          const { data: profRows } = await supabase
            .from('product_profitability')
            .select('product_id, marketplace, sale_price_source, velocity_source')
            .eq('distributor_id', poRow.distributor_id)
            .in('product_id', productIds);
          if (profRows) {
            const provMap: Record<
              string,
              {
                sale_price_source: CalcProfitabilitySalePriceSource | null;
                velocity_source: CalcProfitabilityVelocitySource | null;
              }
            > = {};
            for (const row of profRows as Array<{
              product_id: string;
              /** pp's stored column (slice-B family) — value space is fpc path keys. */
              marketplace: string;
              sale_price_source: CalcProfitabilitySalePriceSource | null;
              velocity_source: CalcProfitabilityVelocitySource | null;
            }>) {
              // Prefer the row matching a line item's actual fulfillment_path;
              // fall back to any row when the line's path isn't represented in
              // cache (historical PO with a deactivated path). Last-write wins
              // on multi-path rows \u2014 operator typically sees one path
              // per line, so the map collision rate is minimal.
              if (!provMap[row.product_id] || linePaths.has(row.marketplace)) {
                provMap[row.product_id] = {
                  sale_price_source: row.sale_price_source,
                  velocity_source: row.velocity_source,
                };
              }
            }
            setProductProvenance(provMap);
          }
        }
      }
    }

    // Check for child revision (if superseded)
    if (poRow.status === 'superseded') {
      const { data: rev } = await supabase
        .from('purchase_orders')
        .select('id, po_number')
        .eq('parent_po_id', poRow.id)
        .limit(1)
        .single();
      if (rev) setRevision(rev as RevisionRef);
    }

    // Check if this is a revision (has parent)
    if (poRow.parent_po_id) {
      const { data: parent } = await supabase
        .from('purchase_orders')
        .select('id, po_number')
        .eq('id', poRow.parent_po_id)
        .single();
      if (parent) setParentPo(parent as RevisionRef);
    }

    // Day 57 — Distributor primary contact for the Email PO action's
    // to_email + the PO PDF's From/To block. Best-effort — a distributor
    // without a contact row falls back to undefined and the email button
    // remains disabled with explanatory tooltip.
    if (poRow.distributor_id) {
      const { data: contact } = await supabase
        .from('distributor_contacts')
        .select('id, name, email, phone')
        .eq('distributor_id', poRow.distributor_id)
        .eq('is_primary', true)
        .maybeSingle();
      setPrimaryContact((contact as DistributorContact | null) ?? null);
    } else {
      setPrimaryContact(null);
    }

    setLoading(false);
  }, [poId]);

  useEffect(() => {
    loadPO();
  }, [loadPO]);

  // Receiving history — load when PO loads (and reload alongside loadPO refreshes
  // since load triggers re-running this effect via po.id dependency).
  useEffect(() => {
    if (!po?.id) return;
    void fetchReceivingHistory(po.id).then(setReceivingHistory);
  }, [po?.id]);

  // Day 51 Phase 5: variance invoice lookup. Hidden for dist_rep per
  // Architecture Rule 4 — variance amount is pricing data. Re-runs when
  // the PO id changes (typically once per page load).
  useEffect(() => {
    if (!po?.id || isDistRep) return;
    void supabase
      .from('invoices')
      .select('id, amount, status')
      .eq('po_id', po.id)
      .eq('status', 'variance_flagged')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setVarianceInvoice(data ?? null);
      });
  }, [po?.id, isDistRep]);

  // Day 56 item 5 — Actual costs loader. Hidden for dist_rep per Architecture
  // Rule 4 (cost data). Refreshable via reloadActualCosts() after save/delete.
  const reloadActualCosts = useCallback(async (): Promise<void> => {
    if (!po?.id || isDistRep) return;
    const { data } = await supabase
      .from('po_actual_costs')
      .select('*')
      .eq('po_id', po.id)
      .order('created_at', { ascending: false });
    setActualCosts((data as POActualCost[]) ?? []);
  }, [po?.id, isDistRep]);

  useEffect(() => {
    void reloadActualCosts();
  }, [reloadActualCosts]);

  // Auto-open a confirm dialog when arriving from the PurchaseOrders list
  // with `?action=...` set (the list only routes here for actions that
  // require the full PO context — e.g. editRequired needs line items).
  useEffect(() => {
    if (loading || !po) return;
    const action = searchParams.get('action');
    if (!action) return;
    if (action === 'editRequired' && po.status === 'approved' && isAdmin) {
      setConfirmAction('editRequired');
    }
    // Strip the param so refresh or tab-restore doesn't re-trigger.
    searchParams.delete('action');
    setSearchParams(searchParams, { replace: true });
  }, [loading, po, isAdmin, searchParams, setSearchParams]);

  // Toast auto-dismiss
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ── Status update helper ────────────────────────────────────
  async function updateStatus(
    newStatus: POStatus,
    extra: Record<string, unknown> = {}
  ): Promise<boolean> {
    if (!po) return false;
    // Routed through the guarded transition RPC (Cluster 4): a stale concurrent
    // write is REJECTED with a typed PO.STALE_CONFLICT, not silently last-write-wins.
    const env = extractEnvelope<{ status: string }>(
      await callRpc('transition_po_status', {
        p_po_id: poId,
        p_expected_status: po.status,
        p_new_status: newStatus,
        p_extra: extra,
      })
    );
    if (!env.isOk) {
      setToast({
        kind: 'error',
        message: env.userErrors[0]?.message ?? 'Status change failed',
      });
      return false;
    }
    return true;
  }

  // ── Action: Submit for Review ───────────────────────────────
  async function handleSubmitForReview(): Promise<void> {
    if (!po || !user) return;
    const ok = await updateStatus('pending_review', {
      submitted_by: user.id,
      submitted_at: new Date().toISOString(),
    });
    if (ok) {
      setToast({ kind: 'success', message: `PO ${po.po_number} submitted for review` });
      const { data: admins } = await supabase.from('profiles').select('id').eq('role', 'admin');
      for (const admin of (admins ?? []) as { id: string }[]) {
        createNotification({
          userId: admin.id,
          event: 'po_submitted',
          title: 'PO Submitted',
          message: `PO ${po.po_number} submitted for review.`,
          link: `/purchasing/purchase-orders/${poId}`,
        });
      }
      loadPO();
    }
    setConfirmAction(null);
  }

  // ── Action: Approve ─────────────────────────────────────────
  async function handleApprove(): Promise<void> {
    if (!po || !user) return;
    // T2.1 — atomic approve guard RPC (from-status + credit-cap + single
    // soft_reserve in one transaction). Replaces updateStatus + the separate
    // create_credit_ledger_entry (which had no cap and could double-fire).
    const env = extractEnvelope<{ status: string }>(
      await callRpc('approve_purchase_order', { p_po_id: poId })
    );
    if (!env.isOk) {
      setToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Failed to approve' });
      setConfirmAction(null);
      return;
    }
    if (po.distributor_id) {
      maybeFireLowCreditWarning(po.distributor_id, po.distributors?.name ?? 'Distributor');
    }
    setToast({ kind: 'success', message: `PO ${po.po_number} approved` });
    triggerTicker('PO approved');
    if (po.submitted_by && po.submitted_by !== user.id) {
      createNotification({
        userId: po.submitted_by,
        event: 'po_approved',
        title: 'PO Approved',
        message: `PO ${po.po_number} has been approved.`,
        link: `/purchasing/purchase-orders/${poId}`,
      });
    }
    loadPO();
    setConfirmAction(null);
  }

  // ── Action: Mark Ordered (Approved → Ordered) ───────────────
  // Per CLAUDE_REFERENCE.md §7, Approved hands off to Ordered when the order
  // is placed with the distributor. Pure status update — soft reserve was
  // already applied at Approval, so no credit ledger entry fires here.
  // Partial-receipt build: accept the shortage on a partially_received PO — the
  // received units are kept/drawn, the outstanding reserve is released, PO → received.
  async function handleCloseShort(): Promise<void> {
    if (!po) return;
    const env = extractEnvelope<{ received_value: number; shortfall_released: number }>(
      await callRpc('close_partial_as_shortage', { p_po_id: poId })
    );
    if (!env.isOk) {
      setToast({
        kind: 'error',
        message: env.userErrors[0]?.message ?? 'Could not close PO as short-received',
      });
    } else {
      setToast({ kind: 'success', message: `PO ${po.po_number} closed — received-only accepted` });
      loadPO();
    }
    setConfirmAction(null);
  }

  async function handleMarkOrdered(): Promise<void> {
    if (!po) return;
    const ok = await updateStatus('ordered');
    if (ok) {
      setToast({ kind: 'success', message: `PO ${po.po_number} marked Ordered` });
      notifySubmitterAndAdmins({
        submitterUserId: po.submitted_by,
        event: 'po_ordered',
        title: `PO ${po.po_number} ordered`,
        message: `PO ${po.po_number} has been placed with the distributor.`,
        link: `/purchasing/purchase-orders/${po.id}`,
      });
      loadPO();
    }
    setConfirmAction(null);
  }

  // ── Action: Mark Awaiting Invoice (Ordered → Awaiting Invoice)
  // Per §7, Ordered → Awaiting Invoice is the canonical hand-off into the
  // invoice-tracking hold stage. No skip to Invoice Received — Processing
  // owns the invoice-upload + actual-cost step that gates invoice_received.
  async function handleMarkAwaitingInvoice(): Promise<void> {
    if (!po) return;
    const ok = await updateStatus('awaiting_invoice');
    if (ok) {
      setToast({ kind: 'success', message: `PO ${po.po_number} moved to Awaiting Invoice` });
      loadPO();
    }
    setConfirmAction(null);
  }

  // ── Action: Mark Processing (Awaiting Invoice → Processing) ─
  async function handleMarkProcessing(): Promise<void> {
    if (!po) return;
    const ok = await updateStatus('processing');
    if (ok) {
      setToast({ kind: 'success', message: `PO ${po.po_number} moved to Processing` });
      notifySubmitterAndAdmins({
        submitterUserId: po.submitted_by,
        event: 'po_processing_complete',
        title: `PO ${po.po_number} processing complete`,
        message: 'Invoice processing has begun for this PO.',
        link: `/purchasing/purchase-orders/${po.id}`,
      });
      loadPO();
    }
    setConfirmAction(null);
  }

  // ── Action: Mark Invoice Received ───────────────────────────
  // Moves Ordered / Awaiting Invoice / Processing → Invoice Received so the
  // PO enters the Receiving queue. Receiving RPC gates on this status.
  async function handleMarkInvoiceReceived(): Promise<void> {
    if (!po) return;
    const ok = await updateStatus('invoice_received');
    if (ok) {
      setToast({ kind: 'success', message: `PO ${po.po_number} moved to Invoice Received` });
      notifySubmitterAndAdmins({
        submitterUserId: po.submitted_by,
        event: 'po_invoice_assigned',
        title: `PO ${po.po_number} invoice assigned`,
        message: 'Invoice has been received and the PO is ready for inventory receipt.',
        link: `/purchasing/purchase-orders/${po.id}`,
      });
      loadPO();
    }
    setConfirmAction(null);
  }

  // ── Action: Reject ──────────────────────────────────────────
  async function handleReject(): Promise<void> {
    if (!po) return;
    const ok = await updateStatus('rejected');
    if (ok) {
      // Capture rejection rationale into entity_notes (replaces former
      // purchase_orders.notes scalar write).
      if (rejectNote.trim()) {
        const noteRpcResult = await supabase.rpc('create_entity_note', {
          p_entity_type: 'purchase_order',
          p_entity_id: po.id,
          p_text: rejectNote.trim(),
          p_visibility: 'all_internal',
        });
        const noteEnv = extractEnvelope<unknown>(noteRpcResult);
        if (!noteEnv.isOk) {
          console.error('[PODetail] reject-note envelope failure:', noteEnv.userErrors[0]?.message);
        }
      }
      setToast({ kind: 'success', message: `PO ${po.po_number} rejected` });
      if (po.submitted_by) {
        createNotification({
          userId: po.submitted_by,
          event: 'po_rejected',
          title: 'PO Rejected',
          message: `PO ${po.po_number} was rejected.${rejectNote ? ' Note: ' + rejectNote : ''}`,
          link: `/purchasing/purchase-orders/${poId}`,
        });
      }
      loadPO();
    }
    setShowRejectModal(false);
    setRejectNote('');
  }

  // ── Action: Save / Update / Delete actual cost ──────────────
  async function handleSaveActualCost(payload: ActualCostFormPayload): Promise<void> {
    if (!po || !user) return;
    if (editingActualCost) {
      // UPDATE — scope/line are locked in edit mode by the modal.
      const { error } = await supabase
        .from('po_actual_costs')
        .update({
          cost_type: payload.cost_type,
          other_subtype: payload.other_subtype,
          amount: payload.amount,
          allocation_method: payload.scope === 'po_level' ? payload.allocation_method : null,
          notes: payload.notes,
        } as never)
        .eq('id', editingActualCost.id);
      if (error) {
        setToast({ kind: 'error', message: error.message });
        return;
      }
      setToast({ kind: 'success', message: 'Actual cost updated' });
    } else {
      // INSERT — PO-level = one row, per-line = one row per selected line.
      const baseRow = {
        po_id: po.id,
        cost_type: payload.cost_type,
        other_subtype: payload.other_subtype,
        amount: payload.amount,
        notes: payload.notes,
        created_by: user.id,
      };
      const rows =
        payload.scope === 'po_level'
          ? [
              {
                ...baseRow,
                line_item_id: null,
                allocation_method: payload.allocation_method,
              },
            ]
          : payload.line_item_ids.map((lid) => ({
              ...baseRow,
              line_item_id: lid,
              allocation_method: null,
            }));
      const { error } = await supabase.from('po_actual_costs').insert(rows as never);
      if (error) {
        setToast({ kind: 'error', message: error.message });
        return;
      }
      setToast({
        kind: 'success',
        message: `Actual cost${rows.length > 1 ? 's' : ''} saved`,
      });
    }
    setShowActualCostModal(false);
    setEditingActualCost(null);
    void reloadActualCosts();
  }

  // ── Day 57 — PDF download + email-to-distributor ───────────
  // Both handlers dynamic-import @react-pdf/renderer + the Document component
  // (per ADR Decision 32) so the PDF chunk only lands when an admin first
  // clicks one of these actions.

  async function handleDownloadPdf(): Promise<void> {
    if (!po) return;
    setPdfActionInFlight('download');
    try {
      const [{ pdf }, { default: POPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../components/pdf/POPdfDocument'),
      ]);
      const blob = await pdf(
        <POPdfDocument
          po={{
            po_number: po.po_number,
            status: po.status,
            order_date: po.order_date,
            expected_delivery_date: po.expected_delivery_date,
            total_cost: po.total_cost,
            total_units: po.total_units,
            total_skus: po.total_skus,
          }}
          lineItems={lineItems.map((li) => ({
            isg_sku: li.isg_sku,
            qty_ordered: li.qty_ordered,
            cost_per_unit: li.cost_per_unit,
            total_cost: li.total_cost,
            fulfillment_path: li.fulfillment_path,
            title: li.title ?? null,
          }))}
          distributor={{
            name: po.distributors?.name ?? null,
            estimated_tax_pct: po.distributors?.estimated_tax_pct ?? null,
          }}
          primaryContact={primaryContact}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO-${po.po_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('[PODetail] download PO PDF error:', e);
      setToast({
        kind: 'error',
        message: 'PDF generation failed. Try again, or contact support if this continues.',
      });
    } finally {
      setPdfActionInFlight(null);
    }
  }

  async function handleEmailPo(): Promise<void> {
    if (!po || !primaryContact?.email) return;
    setShowEmailModal(false);
    setPdfActionInFlight('email');
    try {
      const [{ pdf }, { default: POPdfDocument }] = await Promise.all([
        import('@react-pdf/renderer'),
        import('../../components/pdf/POPdfDocument'),
      ]);
      const blob = await pdf(
        <POPdfDocument
          po={{
            po_number: po.po_number,
            status: po.status,
            order_date: po.order_date,
            expected_delivery_date: po.expected_delivery_date,
            total_cost: po.total_cost,
            total_units: po.total_units,
            total_skus: po.total_skus,
          }}
          lineItems={lineItems.map((li) => ({
            isg_sku: li.isg_sku,
            qty_ordered: li.qty_ordered,
            cost_per_unit: li.cost_per_unit,
            total_cost: li.total_cost,
            fulfillment_path: li.fulfillment_path,
            title: li.title ?? null,
          }))}
          distributor={{
            name: po.distributors?.name ?? null,
            estimated_tax_pct: po.distributors?.estimated_tax_pct ?? null,
          }}
          primaryContact={primaryContact}
        />
      ).toBlob();
      const pdfBase64 = await blobToBase64(blob);
      const toEmail = primaryContact.email;

      const { data: invokeData, error: invokeError } = await supabase.functions.invoke(
        'send-po-email',
        {
          body: { po_id: po.id, to_email: toEmail, pdf_base64: pdfBase64 },
        }
      );
      const result = (invokeData ?? null) as {
        sent?: boolean;
        skipped?: string;
        error?: string;
        id?: string;
      } | null;

      if (invokeError || !result || result.sent === false) {
        const reason = invokeError?.message ?? result?.error ?? result?.skipped ?? 'unknown';
        setToast({ kind: 'error', message: `Email failed — ${reason}` });
        return;
      }

      // Successful send — merge the audit row into external_transactions.
      const merged: ExternalTransactions = {
        ...(po.external_transactions ?? {}),
        distributor_po_sent: {
          sent_at: new Date().toISOString(),
          to_email: toEmail,
          method: 'email',
        },
      };
      const { error: updateError } = await supabase
        .from('purchase_orders')
        .update({ external_transactions: merged } as never)
        .eq('id', po.id);
      if (updateError) {
        console.error('[PODetail] external_transactions update error:', updateError.message);
      }

      // Single audit fire — mirrors handleMarkOrdered. notifications IS the
      // V1 audit log (no separate activity_feed). No notify_distributor
      // conditional — click IS the consent.
      notifySubmitterAndAdmins({
        submitterUserId: po.submitted_by,
        event: 'po_sent_to_distributor',
        title: `PO ${po.po_number} sent to distributor`,
        message: `Emailed to ${toEmail}.`,
        link: `/purchasing/purchase-orders/${po.id}`,
      });

      setToast({ kind: 'success', message: `PO ${po.po_number} emailed to ${toEmail}` });
      loadPO();
    } catch (e) {
      console.error('[PODetail] email PO error:', e);
      setToast({
        kind: 'error',
        message: 'Email failed. Try again, or contact support if this continues.',
      });
    } finally {
      setPdfActionInFlight(null);
    }
  }

  async function handleDeleteActualCost(): Promise<void> {
    if (!deleteCostTarget) return;
    const { error } = await supabase.from('po_actual_costs').delete().eq('id', deleteCostTarget.id);
    if (error) {
      setToast({ kind: 'error', message: error.message });
    } else {
      setToast({ kind: 'success', message: 'Actual cost deleted' });
    }
    setDeleteCostTarget(null);
    void reloadActualCosts();
  }

  // ── Action: Cancel PO ───────────────────────────────────────
  async function handleCancel(payload: CancelPOPayload): Promise<void> {
    if (!po || !user) return;
    const { reason, otherText, notifyDistributor, notifySubmitter } = payload;
    let ok = false;
    if (po.parent_po_id) {
      // Revision: chain_cancel_po writes the user-selected reason on the
      // revision row + 'replaced_by_revision' on the parent (auto-set inside
      // the RPC body — see migration 20260504000002).
      const rpcResult = await callRpc('chain_cancel_po', {
        p_original_po_id: po.parent_po_id,
        p_revision_po_id: poId,
        p_cancelled_by: user.id,
        p_cancellation_reason: reason,
        p_cancellation_other_text: otherText,
      });
      const env = extractEnvelope<unknown>(rpcResult);
      if (!env.isOk) {
        setToast({
          kind: 'error',
          message: env.userErrors[0]?.message ?? 'Cancel failed',
        });
      } else {
        ok = true;
        // DD-04: the parent's reserve_release moved SERVER-SIDE into chain_cancel_po
        // (atomic with the chain cancel; outstanding-only) — no client write to lose.
      }
    } else {
      // Non-revision: the guarded transition (Cluster 4) captures the reason inline
      // and rejects a stale cancel (e.g. on a PO concurrently received).
      ok = await updateStatus('cancelled', {
        cancellation_reason: reason,
        cancellation_other_text: otherText,
      });
    }
    if (ok) {
      // DD-04: the cancel reserve_release moved SERVER-SIDE — the
      // trg_release_reserve_on_cancel trigger fires the outstanding-only release atomically
      // with the status→cancelled change (catches this transition_po_status path + the
      // PurchaseOrders direct-update path). No client fire-and-forget to lose.
      setToast({ kind: 'success', message: `PO ${po.po_number} cancelled` });

      // Notifications gated on the runtime opt-out flags. notifySubmitter
      // controls the submitter notification; notifyDistributor would notify
      // any dist_rep tied to this distributor (no-op when no dist_rep account
      // exists — distributor_contacts table is post-launch).
      if (po.parent_po_id) {
        const notified = new Set<string>();
        if (notifySubmitter && po.submitted_by) {
          createNotification({
            userId: po.submitted_by,
            event: 'po_chain_cancelled',
            title: 'PO Chain Cancelled',
            message: `PO ${po.po_number} and its original PO have been cancelled.`,
            link: `/purchasing/purchase-orders/${poId}`,
          });
          notified.add(po.submitted_by);
        }
        if (notifySubmitter) {
          supabase
            .from('purchase_orders')
            .select('submitted_by')
            .eq('id', po.parent_po_id)
            .single()
            .then(
              ({ data: parent }) => {
                const parentRow = parent as { submitted_by: string | null } | null;
                if (parentRow?.submitted_by && !notified.has(parentRow.submitted_by)) {
                  createNotification({
                    userId: parentRow.submitted_by,
                    event: 'po_chain_cancelled',
                    title: 'PO Chain Cancelled',
                    message: `PO chain cancelled — revision ${po.po_number} and original PO have been cancelled.`,
                    link: `/purchasing/purchase-orders/${po.parent_po_id}`,
                  });
                }
              },
              (e: Error) => console.error('[PODetail] fetch parent submitter error:', e)
            );
        }
      } else if (notifySubmitter && po.submitted_by) {
        createNotification({
          userId: po.submitted_by,
          event: 'po_cancelled',
          title: 'PO Cancelled',
          message: `PO ${po.po_number} has been cancelled.`,
          link: `/purchasing/purchase-orders/${poId}`,
        });
      }
      // Distributor rep notification — find dist_rep profile(s) for this
      // distributor and notify each. Best-effort; no-op when none exist.
      if (notifyDistributor) {
        supabase
          .from('profiles')
          .select('id')
          .eq('distributor_id', po.distributor_id ?? '')
          .eq('role', 'dist_rep')
          .then(
            ({ data: reps }) => {
              for (const rep of (reps as Array<{ id: string }>) || []) {
                createNotification({
                  userId: rep.id,
                  event: 'po_cancelled',
                  title: 'PO Cancelled',
                  message: `PO ${po.po_number} has been cancelled.`,
                  link: `/purchasing/purchase-orders/${poId}`,
                });
              }
            },
            (e: Error) => console.error('[PODetail] dist_rep notify error:', e)
          );
      }
      loadPO();
    }
    setShowCancelModal(false);
  }

  // ── Action: Edit Required (create revision) ─────────────────
  async function handleEditRequired(): Promise<void> {
    if (!po || !user) return;
    const existingRevisions = await supabase
      .from('purchase_orders')
      .select('revision_suffix')
      .eq('parent_po_id', poId ?? '')
      .order('created_at', { ascending: false })
      .limit(1);
    const lastSuffix = (
      existingRevisions.data?.[0] as { revision_suffix?: string | null } | undefined
    )?.revision_suffix;
    const nextSuffix = lastSuffix ? String.fromCharCode(lastSuffix.charCodeAt(0) + 1) : 'A';
    const revNumber = `${po.po_number}-${nextSuffix}`;

    const newPoInsert = {
      po_number: revNumber,
      status: 'draft' as POStatus,
      distributor_id: po.distributor_id,
      total_cost: po.total_cost,
      expected_profit: po.expected_profit,
      total_units: po.total_units,
      total_skus: po.total_skus,
      order_date: po.order_date,
      expected_delivery_date: po.expected_delivery_date,
      fulfillment_path: po.fulfillment_path,
      parent_po_id: poId,
      revision_suffix: nextSuffix,
      created_by: user.id,
    };
    const { data: newPo, error } = await supabase
      .from('purchase_orders')
      .insert(newPoInsert as never)
      .select('id, po_number')
      .single();

    if (error || !newPo) {
      setToast({ kind: 'error', message: `Failed to create revision: ${error?.message}` });
      setConfirmAction(null);
      return;
    }

    const newItems = lineItems.map((i) => ({
      po_id: newPo.id,
      product_id: i.product_id,
      isg_sku: i.isg_sku,
      qty_ordered: i.qty_ordered,
      cost_per_unit: i.cost_per_unit,
      total_cost: i.total_cost,
      fulfillment_path: i.fulfillment_path,
      est_profit_per_unit: i.est_profit_per_unit,
      est_total_profit: i.est_total_profit,
      added_by: user.id,
    }));
    if (newItems.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await supabase.from('po_line_items').insert(newItems as any);
    }

    const superseded = await updateStatus('superseded');
    if (!superseded) {
      // Stale conflict (Cluster 4): the parent changed since load. Roll back the
      // just-created revision (child + its lines) so it isn't orphaned, then abort.
      await supabase.from('po_line_items').delete().eq('po_id', newPo.id);
      await supabase.from('purchase_orders').delete().eq('id', newPo.id);
      setConfirmAction(null);
      return;
    }
    // DD-04: REMOVED the spurious supersede-release. At supersede time the parent has a
    // DRAFT child, which compute_soft_reserve does NOT count as "live" — so the parent
    // STILL reserves (the release here wrote a ledger entry the status model contradicts).
    // The reserve correctly transfers to the revision when the revision goes live; no
    // client release belongs at supersede.

    createNotification({
      userId: user.id,
      event: 'po_edit_required',
      title: 'Edit Required Initiated',
      message: `PO ${po.po_number} superseded. Revision ${revNumber} created.`,
      link: `/purchasing/purchase-orders/${newPo.id}`,
    });

    // Day 56 item 8 — notify_submitter flag (default true). Skip when actor
    // IS the submitter (modal hides the checkbox, but defend for safety).
    if (revisionNotifySubmitter && po.submitted_by && po.submitted_by !== user.id) {
      createNotification({
        userId: po.submitted_by,
        event: 'po_edit_required',
        title: 'Edit Required',
        message: `PO ${po.po_number} requires edits. Revision ${revNumber} has been created.`,
        link: `/purchasing/purchase-orders/${newPo.id}`,
      });
    }

    setToast({ kind: 'success', message: `Revision ${revNumber} created` });
    setConfirmAction(null);
    navigate(`/purchasing/purchase-orders/${newPo.id}/edit`);
  }

  // ── Action: Duplicate ───────────────────────────────────────
  async function handleDuplicate(): Promise<void> {
    if (!po || !user) return;
    const { data: poNum } = await callRpc('generate_po_number');
    if (!poNum) {
      setToast({ kind: 'error', message: 'Failed to generate PO number' });
      return;
    }

    const dupInsert = {
      po_number: poNum as string,
      status: 'draft' as POStatus,
      distributor_id: po.distributor_id,
      total_cost: po.total_cost,
      expected_profit: po.expected_profit,
      total_units: po.total_units,
      total_skus: po.total_skus,
      order_date: new Date().toISOString().split('T')[0],
      fulfillment_path: po.fulfillment_path,
      created_by: user.id,
    };
    const { data: newPo, error } = await supabase
      .from('purchase_orders')
      .insert(dupInsert as never)
      .select('id, po_number')
      .single();

    if (error || !newPo) {
      setToast({ kind: 'error', message: error?.message || 'Duplicate failed' });
      return;
    }

    const newItems = lineItems.map((i) => ({
      po_id: newPo.id,
      product_id: i.product_id,
      isg_sku: i.isg_sku,
      qty_ordered: i.qty_ordered,
      cost_per_unit: i.cost_per_unit,
      total_cost: i.total_cost,
      fulfillment_path: i.fulfillment_path,
      est_profit_per_unit: i.est_profit_per_unit,
      est_total_profit: i.est_total_profit,
      added_by: user.id,
    }));
    if (newItems.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await supabase.from('po_line_items').insert(newItems as any);
    }

    setToast({ kind: 'success', message: 'PO duplicated — review and submit when ready' });
    navigate(`/purchasing/purchase-orders/${newPo.id}/edit`);
  }

  // ── Copy PO number ──────────────────────────────────────────
  function copyPONumber(): void {
    if (po?.po_number) {
      navigator.clipboard.writeText(po.po_number);
      setCopiedPO(true);
      setTimeout(() => setCopiedPO(false), 1500);
    }
  }

  // ── AG Grid theme ───────────────────────────────────────────
  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 11,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  const columnDefs = useMemo<ColDef<LineItemRow>[]>(
    () => [
      {
        headerName: 'ISG SKU',
        field: 'isg_sku',
        width: 120,
        pinned: 'left',
        suppressMovable: true,
        resizable: false,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
      },
      {
        headerName: 'Title',
        field: 'title',
        flex: 1,
        minWidth: 200,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Brand',
        field: 'brand',
        width: 120,
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        headerName: 'Qty',
        field: 'qty_ordered',
        width: 70,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Cost/Unit',
        field: 'cost_per_unit',
        width: 100,
        valueFormatter: (p: ValueFormatterParams<LineItemRow>) => fmt$(p.value as number | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Path',
        field: 'fulfillment_path',
        width: 80,
        valueFormatter: (p: ValueFormatterParams<LineItemRow>) =>
          (p.value ? String(p.value) : '\u2014').toUpperCase(),
        cellStyle: { display: 'flex', alignItems: 'center' } as CellStyle,
      },
      {
        // Day 84.8b P3b — Profit/Unit cellRenderer wraps est_profit_per_unit
        // snapshot value with ProvenanceTooltip mode='cell' + synthetic
        // envelope from productProvenance[product_id].sale_price_source per
        // F2 audit §3 disposition. valueIsSnapshot=true per Lock 2 — rendered
        // value is a SNAPSHOT from po_line_items at PO submit time but the
        // source flag reflects CURRENT product_profitability cache state;
        // tooltip body prepends "Current source: <name>" to signal the
        // divergence (Phase 0.5 §2.2 / #DBN-ζ disk-truth-corrected Variant B).
        // staleThresholdDays = 90 default per Day 84.8a precedent; synthetic
        // envelope carries no set_at so threshold is structurally inert.
        headerName: 'Profit/Unit',
        field: 'est_profit_per_unit',
        width: 100,
        cellRenderer: (p: ICellRendererParams<LineItemRow>): JSX.Element => {
          const value = p.value as number | null | undefined;
          const formatted = fmt$(value);
          const data = p.data;
          const baseContent = <span>{formatted}</span>;
          if (value == null || !data?.product_id) return baseContent;
          const sourceFlag = productProvenance[data.product_id]?.sale_price_source;
          if (!sourceFlag) return baseContent;
          const syntheticProv: ProvenanceMap = {
            est_profit_per_unit: { source: sourceFlag },
          };
          return (
            <ProvenanceTooltip
              provenance={syntheticProv}
              fieldKey="est_profit_per_unit"
              staleThresholdDays={90}
              mode="cell"
              valueIsSnapshot={true}
            >
              {baseContent}
            </ProvenanceTooltip>
          );
        },
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
      {
        headerName: 'Total',
        field: 'total_cost',
        width: 100,
        valueFormatter: (p: ValueFormatterParams<LineItemRow>) => fmt$(p.value as number | null),
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontFamily: "'JetBrains Mono', monospace",
        } as CellStyle,
        headerClass: 'ag-right-aligned-header',
      },
    ],
    [productProvenance]
  );

  // ── Day 56 — Profitability variance memo ─────────────────────
  // Read-time computation only — no write-back to product_profitability.
  // Apportionment formulas are deterministic (split_by_qty, split_by_cost).
  // 'manual' allocation rows skip per-line apportionment by design — they
  // appear in the PO-total + the PO-level breakdown but not on any line.
  // Declared here, BEFORE the early returns below, to satisfy rules-of-hooks.
  const variance = useMemo(() => {
    if (!po || isDistRep || actualCosts.length === 0 || lineItems.length === 0) {
      return null;
    }
    const ctx = { distributorId: po.distributor_id ?? undefined };
    const num = (key: string, fallback: number): number => {
      const v = Number(paramStore.resolveKey(key, ctx)?.param.value);
      return isFinite(v) ? v : fallback;
    };
    // Engine COALESCE chain — see calculate_profitability /
    // wire_safety_buffer migration. Defaults match the engine fallbacks.
    const safetyBufferPct = num('safety_buffer_pct', 0); // percent (e.g. 5)
    const taxPct = po.distributors?.estimated_tax_pct
      ? Number(po.distributors.estimated_tax_pct) * 100
      : 0; // decimal in DB, percent in math
    const supplierShipping = num('supplier_shipping_per_unit', 0);
    const inboundFba = num('inbound_shipping_per_unit_fba', 0);
    const inboundWfs = num('inbound_shipping_per_unit_wfs', 0);
    const fbaPrep = num('fba_prep_fee_per_unit', 0);
    const miscFee = num('misc_fee_per_unit', 0);

    // Per-line estimated cost using the engine's chain at line-aggregate
    // granularity. Channel-conditional adders (inbound shipping, prep) gate
    // on the line's primary fulfillment_path. Multi-path allocations are
    // approximated at the line level — V1 trade-off.
    const lineEstimates: Record<string, number> = {};
    let sumQty = 0;
    let sumQtyCost = 0;
    for (const li of lineItems) {
      const qty = Number(li.qty_ordered || 0);
      const cost = Number(li.cost_per_unit || 0);
      const path = li.fulfillment_path || 'amazon_fba';
      const productCost = qty * cost * (1 + (safetyBufferPct + taxPct) / 100);
      let perUnitAdders = supplierShipping + miscFee;
      if (path === 'amazon_fba' || path === 'walmart_wfs') {
        perUnitAdders += path === 'walmart_wfs' ? inboundWfs : inboundFba;
      }
      if (path === 'amazon_fba') perUnitAdders += fbaPrep;
      const est = productCost + qty * perUnitAdders;
      lineEstimates[li.id] = est;
      sumQty += qty;
      sumQtyCost += qty * cost;
    }
    const poEstimated = Object.values(lineEstimates).reduce((s, v) => s + v, 0);

    // Apportionment of PO-level actuals to lines. 'manual' rows skip.
    const lineApportioned: Record<string, number> = {};
    for (const li of lineItems) lineApportioned[li.id] = 0;

    let poLevelManualTotal = 0;
    for (const cost of actualCosts) {
      const amt = Number(cost.amount || 0);
      if (cost.line_item_id) {
        lineApportioned[cost.line_item_id] = (lineApportioned[cost.line_item_id] ?? 0) + amt;
      } else if (cost.allocation_method === 'split_by_qty' && sumQty > 0) {
        for (const li of lineItems) {
          const qty = Number(li.qty_ordered || 0);
          lineApportioned[li.id] += amt * (qty / sumQty);
        }
      } else if (cost.allocation_method === 'split_by_cost' && sumQtyCost > 0) {
        for (const li of lineItems) {
          const qty = Number(li.qty_ordered || 0);
          const c = Number(li.cost_per_unit || 0);
          lineApportioned[li.id] += amt * ((qty * c) / sumQtyCost);
        }
      } else {
        // 'manual' or PO-level with no apportionable basis (sumQty=0 etc.) —
        // shows in PO total only.
        poLevelManualTotal += amt;
      }
    }

    const totalActualsExtra = actualCosts.reduce((s, r) => s + Number(r.amount || 0), 0);
    const poActual = poEstimated + totalActualsExtra;
    const variancePct = poEstimated > 0 ? (totalActualsExtra / poEstimated) * 100 : 0;

    return {
      poEstimated,
      poActual,
      varianceAmount: totalActualsExtra,
      variancePct,
      lineEstimates,
      lineApportioned,
      poLevelManualTotal,
    };
  }, [po, isDistRep, actualCosts, lineItems, paramStore]);

  // ── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-ink-muted">Loading PO...</div>
        </div>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="flex flex-col h-full -m-4">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-ink-muted">PO not found</div>
        </div>
      </div>
    );
  }

  const s = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;
  const isEditable = EDITABLE_STATUSES.includes(po.status);
  const readOnlyMsg = READ_ONLY_MESSAGES[po.status];
  const totalCost = Number(po.total_cost || 0);
  const totalProfit = Number(po.expected_profit || 0);
  const roi = totalCost > 0 ? (totalProfit / totalCost) * 100 : 0;

  // Derived fulfillment-path display. Aggregates from po_line_item_allocations
  // (loaded above into allocationTotals). Single path → label; 2 paths →
  // "Mixed (FBA + FBM)"; 3+ paths → "Mixed (3 paths)"; zero paths (legacy
  // POs without allocation rows) → fall back to po.fulfillment_path. The
  // PO-level fulfillment_path column is now display-deprecated; full removal
  // tracked as a post-launch BACKLOG item.
  const fulfillmentPathDisplay = ((): string => {
    // Day 76 P5.1 Phase 9B — local var renamed from activePaths (was
    // shadowing the useSettingsConfig export); pathLabel helper replaces
    // hardcoded PATH_LABEL lookup, falls back to path_key.toUpperCase() for
    // historical-data carve-out (deactivated keys not in fpc).
    const usedPaths = Object.entries(allocationTotals)
      .filter(([, qty]) => qty > 0)
      .map(([path]) => path);
    if (usedPaths.length === 0) {
      return po.fulfillment_path ? po.fulfillment_path.toUpperCase() : '—';
    }
    if (usedPaths.length === 1) {
      return pathLabel(usedPaths[0]);
    }
    if (usedPaths.length === 2) {
      const labels = usedPaths.map((p) => pathLabel(p));
      return `Mixed (${labels.join(' + ')})`;
    }
    return `Mixed (${usedPaths.length} paths)`;
  })();

  const terminalStatuses: POStatus[] = ['closed', 'cancelled', 'superseded', 'received'];

  return (
    <div className="flex flex-col h-full -m-4">
      {/* Toast */}
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Day 98.5b — concurrent-edit banner (another user's UPDATE → refresh) */}
      <ConcurrentEditBanner table="purchase_orders" recordId={poId} onRefresh={loadPO} />

      {/* Toolbar */}
      <div className="flex-shrink-0 px-4 py-2 bg-surface-1 border-b border-border">
        {/* Back nav */}
        <button
          onClick={() => navigate('/purchasing/purchase-orders')}
          className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-primary transition-colors mb-1 cursor-pointer"
        >
          <ChevronLeft size={12} /> Back to Purchase Orders
        </button>
        {/* Title row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold text-ink-bright">PO {po.po_number}</h1>
            <button
              onClick={copyPONumber}
              className="p-1 rounded text-ink-muted hover:text-ink-primary transition-colors cursor-pointer"
              aria-label="Copy PO number"
              title="Copy PO number"
            >
              {copiedPO ? <Check size={12} className="text-success" /> : <Copy size={12} />}
            </button>
            <span className={clsx('px-1.5 py-0.5 rounded text-2xs font-medium', s.cls)}>
              {s.label}
            </span>
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Duplicate — always available (user+). Day 64.5: hide for viewer. */}
            {!isViewer && (
              <button onClick={handleDuplicate} className="arbi-btn-ghost text-xs">
                Duplicate
              </button>
            )}

            {/* Day 56 — Add Actual Cost. Admin-only, dist_rep gated, visible
                only across the 6 statuses where actuals can be logged. */}
            {isAdmin && !isDistRep && ACTUALS_WINDOW_STATUSES.includes(po.status) && (
              <button
                onClick={() => {
                  setEditingActualCost(null);
                  setShowActualCostModal(true);
                }}
                className="arbi-btn-ghost text-xs"
                data-testid="add-actual-cost-button"
              >
                Add Actual Cost
              </button>
            )}

            {/* Day 57 — Download PO PDF + Email PO to Distributor.
                Visible from approved onward, admin-only. Always-clickable;
                button disables briefly during action in flight. */}
            {isAdmin && STATUSES_PDF_VISIBLE.includes(po.status) && (
              <>
                <button
                  onClick={handleDownloadPdf}
                  disabled={pdfActionInFlight !== null}
                  className="arbi-btn-ghost text-xs"
                  data-testid="po-download-pdf-button"
                >
                  {pdfActionInFlight === 'download' ? 'Generating…' : 'Download PDF'}
                </button>
                <button
                  onClick={() => setShowEmailModal(true)}
                  disabled={pdfActionInFlight !== null || !primaryContact?.email}
                  title={
                    !primaryContact?.email
                      ? 'No primary distributor contact email — add one in Directory'
                      : undefined
                  }
                  className="arbi-btn-ghost text-xs"
                  data-testid="po-email-button"
                >
                  {pdfActionInFlight === 'email' ? 'Sending…' : 'Email Distributor'}
                </button>
                {po.external_transactions?.distributor_po_sent ? (
                  <span className="text-2xs text-ink-muted" data-testid="po-sent-badge">
                    Sent {fmtRelative(po.external_transactions.distributor_po_sent.sent_at)} to{' '}
                    {po.external_transactions.distributor_po_sent.to_email}
                  </span>
                ) : null}
              </>
            )}

            {/* Draft / Rejected: Edit + Submit */}
            {isEditable && !isViewer && (
              <>
                <button
                  onClick={() => navigate(`/purchasing/purchase-orders/${poId}/edit`)}
                  className="arbi-btn-primary text-xs"
                >
                  Edit PO
                </button>
                <button
                  onClick={() => setConfirmAction('submit')}
                  className="arbi-btn-primary text-xs"
                >
                  Submit for Review
                </button>
              </>
            )}

            {/* Pending Review: Approve + Reject (admin) */}
            {po.status === 'pending_review' && isAdmin && (
              <>
                <button
                  onClick={() => setConfirmAction('approve')}
                  className="arbi-btn-primary text-xs"
                >
                  Approve
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="arbi-btn-ghost text-xs text-danger"
                >
                  Reject
                </button>
              </>
            )}

            {/* Approved: Mark Ordered (forward) + Edit Required (revision) — admin */}
            {po.status === 'approved' && isAdmin && (
              <>
                <button
                  onClick={() => setConfirmAction('markOrdered')}
                  className="arbi-btn-primary text-xs"
                >
                  Mark Ordered
                </button>
                <button
                  onClick={() => setConfirmAction('editRequired')}
                  className="arbi-btn-ghost text-xs"
                >
                  Edit Required
                </button>
              </>
            )}

            {/* Ordered: Mark Awaiting Invoice (admin). Per §7 the canonical
                forward path is approved → ordered → awaiting_invoice →
                processing → invoice_received — no skip to invoice_received. */}
            {po.status === 'ordered' && isAdmin && (
              <button
                onClick={() => setConfirmAction('markAwaitingInvoice')}
                className="arbi-btn-primary text-xs"
              >
                Mark Awaiting Invoice
              </button>
            )}

            {/* Processing: Mark Invoice Received */}
            {po.status === 'processing' && !isViewer && (
              <button
                onClick={() => setConfirmAction('markInvoiceReceived')}
                className="arbi-btn-primary text-xs"
              >
                Mark Invoice Received
              </button>
            )}

            {/* Awaiting Invoice: Mark Processing */}
            {po.status === 'awaiting_invoice' && !isViewer && (
              <button
                onClick={() => setConfirmAction('markProcessing')}
                className="arbi-btn-primary text-xs"
              >
                Mark Processing
              </button>
            )}

            {/* Invoice Received: Begin Receiving */}
            {po.status === 'invoice_received' && !isViewer && (
              <button
                onClick={() => navigate(`/purchasing/receiving/${poId}`)}
                className="arbi-btn-primary text-xs"
              >
                Begin Receiving
              </button>
            )}

            {/* Partially Received: Continue Receiving */}
            {po.status === 'partially_received' && !isViewer && (
              <button
                onClick={() => navigate(`/purchasing/receiving/${poId}`)}
                className="arbi-btn-primary text-xs"
              >
                Continue Receiving
              </button>
            )}

            {/* Partially Received: accept the shortage (no more units coming) */}
            {po.status === 'partially_received' && !isViewer && (
              <button
                onClick={() => setConfirmAction('closeShort')}
                className="arbi-btn-ghost text-xs"
              >
                Close as Short-Received
              </button>
            )}

            {/* Discrepancy: Resolve Discrepancy (navigate to queue) */}
            {po.status === 'discrepancy' && !isViewer && (
              <button
                onClick={() => navigate('/purchasing/discrepancies')}
                className="arbi-btn-primary text-xs"
              >
                Resolve Discrepancy
              </button>
            )}

            {/* Cancel — admin, any non-terminal status */}
            {isAdmin && !terminalStatuses.includes(po.status) && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="arbi-btn-ghost text-xs text-danger"
              >
                Cancel PO
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Revision banners */}
      {po.status === 'superseded' && revision && (
        <div className="px-4 py-2 bg-info/10 border-b border-info/30 flex items-center gap-2">
          <Info size={14} className="text-info flex-shrink-0" />
          <span className="text-xs text-info">
            This PO has been superseded by revision{' '}
            <button
              onClick={() => navigate(`/purchasing/purchase-orders/${revision.id}`)}
              className="font-medium underline cursor-pointer"
            >
              {revision.po_number}
            </button>
          </span>
        </div>
      )}
      {parentPo && (
        <div className="px-4 py-2 bg-info/10 border-b border-info/30 flex items-center gap-2">
          <Info size={14} className="text-info flex-shrink-0" />
          <span className="text-xs text-info">
            This is a revision of{' '}
            <button
              onClick={() => navigate(`/purchasing/purchase-orders/${parentPo.id}`)}
              className="font-medium underline cursor-pointer"
            >
              {parentPo.po_number}
            </button>
          </span>
        </div>
      )}

      {/* Read-only status message */}
      {readOnlyMsg && (
        <div className="px-4 py-2 bg-surface-2 border-b border-border-subtle flex items-center gap-2">
          <AlertTriangle size={14} className="text-ink-muted flex-shrink-0" />
          <span className="text-xs text-ink-muted">{readOnlyMsg}</span>
        </div>
      )}

      {/* Content area — two columns */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex gap-4">
          {/* Left: Details + Line Items */}
          <div className="flex-1 min-w-0 space-y-4">
            {/* PO Details card */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">PO Details</h2>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-xs">
                <div>
                  <span className="text-ink-muted">PO Number</span>
                  <p className="font-mono text-ink-bright">{po.po_number}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Distributor</span>
                  <p className="text-ink-primary">{po.distributors?.name || '\u2014'}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Fulfillment Path</span>
                  <p className="text-ink-primary">{fulfillmentPathDisplay}</p>
                </div>
                <div>
                  <span className="text-ink-muted">Order Date</span>
                  <p className="font-mono text-ink-secondary">{fmtDate(po.order_date)}</p>
                </div>
                {po.expected_delivery_date && (
                  <div>
                    <span className="text-ink-muted">Expected Delivery</span>
                    <p className="font-mono text-ink-secondary">
                      {fmtDate(po.expected_delivery_date)}
                    </p>
                  </div>
                )}
                {po.submitted_at && (
                  <div>
                    <span className="text-ink-muted">Submitted</span>
                    <p className="font-mono text-ink-secondary">{fmtDate(po.submitted_at)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <NotesPanel entityType="purchase_order" entityId={po.id} />

            {/* Day 56 — Actual Costs card. Hidden for dist_rep (Architecture
                Rule 4 — cost data). Renders only when the PO has at least
                one logged actual cost. The Add button lives in the action
                toolbar above. */}
            {!isDistRep && actualCosts.length > 0 && (
              <div
                className="bg-surface-1 border border-border rounded-lg"
                data-testid="actual-costs-card"
              >
                <div className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-ink-bright">
                    Actual Costs{' '}
                    <span className="text-ink-muted font-normal ml-1">
                      {actualCosts.length} {actualCosts.length === 1 ? 'entry' : 'entries'}
                    </span>
                  </h2>
                  <span
                    className="text-xs font-mono text-ink-bright"
                    data-testid="actual-costs-total"
                  >
                    Total {fmt$(actualCosts.reduce((s, r) => s + Number(r.amount || 0), 0))}
                  </span>
                </div>
                <div className="divide-y divide-border-subtle">
                  {actualCosts.map((row) => {
                    const lineMatch = row.line_item_id
                      ? lineItems.find((li) => li.id === row.line_item_id)
                      : null;
                    const scopeLabel = row.line_item_id
                      ? `Line · ${lineMatch?.isg_sku || row.line_item_id.slice(0, 8)}`
                      : `PO-level · ${row.allocation_method ?? '—'}`;
                    const typeLabel =
                      row.cost_type === 'other' && row.other_subtype
                        ? `Other (${row.other_subtype})`
                        : row.cost_type;
                    return (
                      <div
                        key={row.id}
                        className="px-4 py-2 flex items-center gap-3 text-xs"
                        data-testid={`actual-costs-row-${row.id}`}
                      >
                        <span className="capitalize text-ink-primary min-w-[100px]">
                          {typeLabel}
                        </span>
                        <span className="text-ink-muted flex-1 min-w-0 truncate">
                          {scopeLabel}
                          {row.notes ? <span className="ml-2">· {row.notes}</span> : null}
                        </span>
                        <span className="font-mono text-ink-bright min-w-[80px] text-right">
                          {fmt$(Number(row.amount))}
                        </span>
                        {isAdmin && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setEditingActualCost(row);
                                setShowActualCostModal(true);
                              }}
                              data-testid={`actual-costs-edit-${row.id}`}
                              className="text-2xs text-ink-muted hover:text-accent transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteCostTarget(row)}
                              data-testid={`actual-costs-delete-${row.id}`}
                              className="text-2xs text-ink-muted hover:text-danger transition-colors"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Day 56 — Profitability Variance card. Read-time computation
                only — no write-back to product_profitability. Shown only
                when ≥1 actual cost row exists (paired with the Actual Costs
                card above). Hidden for dist_rep (Architecture Rule 4). */}
            {!isDistRep && variance && (
              <div
                className="bg-surface-1 border border-border rounded-lg"
                data-testid="profitability-variance-card"
              >
                <div className="px-4 py-3 border-b border-border-subtle">
                  <h2 className="text-sm font-semibold text-ink-bright">Profitability Variance</h2>
                  <p className="text-2xs text-ink-muted mt-0.5">
                    Read-time view of estimated vs actual cost. Manual-allocation rows show in PO
                    total only — not apportioned to lines.
                  </p>
                </div>

                {/* Top stats */}
                <div className="grid grid-cols-4 divide-x divide-border-subtle border-b border-border-subtle">
                  <div className="px-4 py-3" data-testid="variance-stat-estimated">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">
                      Estimated
                    </div>
                    <div className="text-sm font-mono text-ink-bright mt-0.5">
                      {fmt$(variance.poEstimated)}
                    </div>
                  </div>
                  <div className="px-4 py-3" data-testid="variance-stat-actual">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">Actual</div>
                    <div className="text-sm font-mono text-ink-bright mt-0.5">
                      {fmt$(variance.poActual)}
                    </div>
                  </div>
                  <div className="px-4 py-3" data-testid="variance-stat-amount">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">
                      Variance $
                    </div>
                    <div
                      className={clsx(
                        'text-sm font-mono mt-0.5',
                        variance.varianceAmount > 0 ? 'text-danger' : 'text-success'
                      )}
                    >
                      {fmt$(variance.varianceAmount)}
                    </div>
                  </div>
                  <div className="px-4 py-3" data-testid="variance-stat-pct">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted">
                      Variance %
                    </div>
                    <div
                      className={clsx(
                        'text-sm font-mono mt-0.5',
                        variance.variancePct > 0 ? 'text-danger' : 'text-success'
                      )}
                    >
                      {fmtPct(variance.variancePct)}
                    </div>
                  </div>
                </div>

                {/* Per-line table */}
                <div className="px-4 py-3">
                  {lineItems.length === 0 ? (
                    <p className="text-xs text-ink-muted">No lines to apportion against</p>
                  ) : (
                    <div className="space-y-1">
                      <div className="grid grid-cols-[1fr_repeat(4,minmax(0,90px))] gap-3 pb-2 border-b border-border-subtle text-2xs uppercase tracking-wider text-ink-muted">
                        <span>ISG SKU</span>
                        <span className="text-right">Estimated</span>
                        <span className="text-right">Actuals</span>
                        <span className="text-right">Variance $</span>
                        <span className="text-right">Variance %</span>
                      </div>
                      {lineItems.map((li) => {
                        const est = variance.lineEstimates[li.id] ?? 0;
                        const apportioned = variance.lineApportioned[li.id] ?? 0;
                        const linePct = est > 0 ? (apportioned / est) * 100 : 0;
                        return (
                          <div
                            key={li.id}
                            className="grid grid-cols-[1fr_repeat(4,minmax(0,90px))] gap-3 py-1 text-xs items-baseline"
                            data-testid={`variance-line-row-${li.id}`}
                          >
                            <span className="font-mono text-ink-primary truncate">
                              {li.isg_sku || li.id.slice(0, 8)}
                            </span>
                            <span className="text-right font-mono text-ink-primary">
                              {fmt$(est)}
                            </span>
                            <span className="text-right font-mono text-ink-primary">
                              {fmt$(apportioned)}
                            </span>
                            <span
                              className={clsx(
                                'text-right font-mono',
                                apportioned > 0 ? 'text-danger' : 'text-ink-muted'
                              )}
                            >
                              {fmt$(apportioned)}
                            </span>
                            <span
                              className={clsx(
                                'text-right font-mono',
                                linePct > 0 ? 'text-danger' : 'text-ink-muted'
                              )}
                            >
                              {fmtPct(linePct)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* PO-level breakdown — manual rows highlighted */}
                {actualCosts.some((r) => !r.line_item_id) && (
                  <div className="px-4 py-3 border-t border-border-subtle">
                    <div className="text-2xs uppercase tracking-wider text-ink-muted mb-2">
                      PO-Level Rows
                    </div>
                    <div className="space-y-1">
                      {actualCosts
                        .filter((r) => !r.line_item_id)
                        .map((row) => {
                          const isManual = row.allocation_method === 'manual';
                          const typeLabel =
                            row.cost_type === 'other' && row.other_subtype
                              ? `Other (${row.other_subtype})`
                              : row.cost_type;
                          return (
                            <div
                              key={row.id}
                              className={clsx(
                                'flex items-center gap-3 px-2 py-1.5 rounded text-xs',
                                isManual && 'bg-warning/10 border border-warning/20'
                              )}
                              data-testid={`variance-po-level-row-${row.id}`}
                            >
                              <span className="capitalize text-ink-primary min-w-[100px]">
                                {typeLabel}
                              </span>
                              <span
                                className={clsx(
                                  'text-2xs px-1.5 py-0.5 rounded',
                                  isManual
                                    ? 'bg-warning/20 text-warning'
                                    : 'bg-accent/15 text-accent'
                                )}
                                title={
                                  isManual
                                    ? 'Audit-only — not apportioned to lines per V1 semantic'
                                    : undefined
                                }
                              >
                                {row.allocation_method}
                              </span>
                              <span className="text-ink-muted flex-1 min-w-0 truncate">
                                {row.notes || ''}
                              </span>
                              <span className="font-mono text-ink-bright">
                                {fmt$(Number(row.amount))}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                    {variance.poLevelManualTotal > 0 && (
                      <p className="text-2xs text-ink-muted mt-2">
                        {fmt$(variance.poLevelManualTotal)} in manual rows is included in the PO
                        total above but not apportioned to lines.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Line Items */}
            <div className="bg-surface-1 border border-border rounded-lg">
              <div className="px-4 py-3 border-b border-border-subtle">
                <h2 className="text-sm font-semibold text-ink-bright">
                  Line Items{' '}
                  <span className="text-ink-muted font-normal ml-1">
                    {lineItems.length} {lineItems.length === 1 ? 'item' : 'items'}
                  </span>
                </h2>
              </div>
              <div style={{ height: Math.min(420, Math.max(200, lineItems.length * 42 + 42)) }}>
                <AgGridReact<LineItemRow>
                  theme={gridTheme}
                  columnDefs={columnDefs}
                  rowData={lineItems}
                  headerHeight={36}
                  rowHeight={42}
                  animateRows={false}
                  suppressCellFocus={true}
                  suppressHorizontalScroll={true}
                  domLayout="normal"
                />
              </div>
            </div>

            {/* Receiving History */}
            <div className="bg-surface-1 border border-border rounded-lg p-4">
              <h2 className="text-sm font-semibold text-ink-bright mb-3">Receiving History</h2>
              {receivingHistory.length === 0 ? (
                <p className="text-xs text-ink-muted">No receiving records yet</p>
              ) : (
                <ul className="space-y-1.5">
                  {receivingHistory.map((h) => (
                    <li key={h.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/purchasing/receiving/${po.id}`)}
                        className="w-full flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-surface-2 transition-colors text-left"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="font-mono text-ink-secondary whitespace-nowrap">
                            {fmtDate(h.received_at)}
                          </span>
                          <span className="text-ink-primary truncate">
                            {h.received_by_name || 'Unknown'}
                          </span>
                          <span
                            className={clsx(
                              'px-1.5 py-0.5 rounded text-2xs font-medium',
                              h.status === 'submitted'
                                ? 'bg-success/10 text-success'
                                : 'bg-surface-3 text-ink-muted'
                            )}
                          >
                            {h.status}
                          </span>
                        </div>
                        <span className="text-ink-muted font-mono whitespace-nowrap">
                          {h.line_item_count} item{h.line_item_count !== 1 ? 's' : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Right: Summary */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-0 space-y-4">
              {/* Day 51 Phase 5: Invoice variance card. Renders only when
                  rule_flag_invoice_variance has flagged an invoice on this
                  PO. Hidden for dist_rep per Architecture Rule 4 (variance
                  amount = pricing data). */}
              {!isDistRep && varianceInvoice && (
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                  <h3 className="text-2xs font-semibold text-warning uppercase tracking-wider mb-2">
                    Invoice Variance
                  </h3>
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-muted">PO Total</span>
                      <span className="font-mono text-ink-primary">{fmt$(po.total_cost)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-ink-muted">Invoice</span>
                      <span className="font-mono text-ink-primary">
                        {fmt$(varianceInvoice.amount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs pt-2 border-t border-border-subtle">
                      <span className="text-ink-muted">Variance</span>
                      <span className="font-mono text-warning font-medium">
                        {(() => {
                          const inv = Number(varianceInvoice.amount ?? 0);
                          const poTotal = Number(po.total_cost ?? 0);
                          const delta = inv - poTotal;
                          const pct = poTotal === 0 ? 0 : (delta / poTotal) * 100;
                          return `${fmt$(delta)} (${pct.toFixed(1)}%)`;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* PO Summary card */}
              <div className="bg-surface-1 border border-border rounded-lg p-4">
                <h2 className="text-sm font-semibold text-ink-bright mb-3">Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Total SKUs</span>
                    <span className="font-mono text-ink-primary">{po.total_skus || 0}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Total Units</span>
                    <span className="font-mono text-ink-primary">{po.total_units || 0}</span>
                  </div>
                  <div className="border-t border-border-subtle my-2" />
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Total Cost</span>
                    <span className="font-mono text-ink-bright font-medium">
                      {fmt$(po.total_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Est. Profit</span>
                    <span
                      className={clsx(
                        'font-mono font-medium',
                        totalProfit > 0 ? 'text-success' : 'text-danger'
                      )}
                    >
                      {fmt$(po.expected_profit)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-ink-muted">Est. ROI</span>
                    <span
                      className={clsx(
                        'font-mono font-medium',
                        roi > 0 ? 'text-success' : 'text-danger'
                      )}
                    >
                      {fmtPct(roi)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        open={confirmAction === 'submit'}
        title="Submit for Review"
        message={`Submit PO ${po.po_number} for admin approval?`}
        onConfirm={handleSubmitForReview}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'approve'}
        title="Approve PO"
        message={`Approve PO ${po.po_number}? Soft reserve will be applied.`}
        onConfirm={handleApprove}
        onCancel={() => setConfirmAction(null)}
      />
      <CancelPOModal
        open={showCancelModal}
        poLabel={`${po.po_number} · ${po.distributors?.name ?? 'Distributor'}`}
        hideNotifySubmitter={po.submitted_by === user?.id}
        onConfirm={handleCancel}
        onCancel={() => setShowCancelModal(false)}
      />
      <ConfirmDialog
        open={confirmAction === 'editRequired'}
        title="Edit Required"
        message={`This will create a revision of ${po.po_number} and move the original to Superseded status. The soft reserve will remain active until the revision is approved. Continue?`}
        extraContent={
          po.submitted_by !== user?.id ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="edit-required-notify-submitter"
                checked={revisionNotifySubmitter}
                onChange={(e) => setRevisionNotifySubmitter(e.target.checked)}
                className="accent-accent"
              />
              Notify PO submitter
            </label>
          ) : undefined
        }
        onConfirm={handleEditRequired}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'markOrdered'}
        title="Mark Ordered"
        message={`Move PO ${po.po_number} to Ordered? This records that the order was placed with the distributor.`}
        onConfirm={handleMarkOrdered}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'markAwaitingInvoice'}
        title="Mark Awaiting Invoice"
        message={`Move PO ${po.po_number} to Awaiting Invoice? The PO will hold here until the distributor invoice arrives.`}
        onConfirm={handleMarkAwaitingInvoice}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'markProcessing'}
        title="Mark Processing"
        message={`Move PO ${po.po_number} to Processing? Upload the distributor invoice via Files and enter any actual-cost adjustments.`}
        onConfirm={handleMarkProcessing}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'closeShort'}
        title="Close as Short-Received"
        message={`Close PO ${po.po_number} as short-received? The received units are kept and drawn; the outstanding (un-received) reserve is released. This cannot be undone.`}
        onConfirm={handleCloseShort}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === 'markInvoiceReceived'}
        title="Mark Invoice Received"
        message={`Move PO ${po.po_number} to Invoice Received? Actual costs will be locked and the PO will enter the Receiving queue.`}
        onConfirm={handleMarkInvoiceReceived}
        onCancel={() => setConfirmAction(null)}
      />

      {/* Day 56 — Actual costs add/edit modal + delete confirm */}
      <ActualCostsModal
        open={showActualCostModal}
        poNumber={po.po_number}
        lineItems={lineItems.map((li) => ({
          id: li.id,
          isg_sku: li.isg_sku,
          qty_ordered: li.qty_ordered,
        }))}
        existingCost={editingActualCost}
        onConfirm={handleSaveActualCost}
        onCancel={() => {
          setShowActualCostModal(false);
          setEditingActualCost(null);
        }}
      />
      <ConfirmDialog
        open={!!deleteCostTarget}
        title="Delete Actual Cost"
        message={
          deleteCostTarget
            ? `Delete ${deleteCostTarget.cost_type} cost of ${fmt$(Number(deleteCostTarget.amount))}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        onConfirm={handleDeleteActualCost}
        onCancel={() => setDeleteCostTarget(null)}
      />

      {/* Day 57 — Email PO confirmation */}
      <ConfirmDialog
        open={showEmailModal}
        title="Email PO to Distributor"
        message={
          po && primaryContact?.email
            ? `Email PO ${po.po_number} to ${primaryContact.email}? PDF attachment will be sent.`
            : ''
        }
        confirmLabel="Send Email"
        onConfirm={handleEmailPo}
        onCancel={() => setShowEmailModal(false)}
      />

      {/* Reject modal with note */}
      {showRejectModal && (
        <div
          className="fixed inset-0 bg-surface-0/80 flex items-center justify-center"
          style={{ zIndex: 'var(--z-modal, 200)' }}
        >
          <div
            className="bg-surface-1 border border-border rounded-xl shadow-lg w-full max-w-md p-6"
            role="dialog"
            aria-modal="true"
            aria-label={`Reject PO ${po.po_number}`}
          >
            <h3 className="text-sm font-semibold text-ink-bright mb-3">Reject PO {po.po_number}</h3>
            <label
              htmlFor="po-reject-note"
              className="block text-xs font-medium text-ink-secondary mb-1.5"
            >
              Rejection Note (optional)
            </label>
            <textarea
              id="po-reject-note"
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              className="arbi-input w-full text-xs h-20 resize-none"
              placeholder="Reason for rejection..."
            />
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-border-subtle">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectNote('');
                }}
                className="arbi-btn-ghost text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="arbi-btn-primary text-xs bg-danger hover:bg-danger/80"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
